import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingApprovalMode } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { zodResolver } from '@hookform/resolvers/zod'
import type { TFunction } from 'i18next'
import { type FieldErrors, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'
import { z } from 'zod'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Alert,
  FieldRoot,
  HStack,
  Input,
  Link,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  FieldErrorText as RawFieldErrorText,
  FieldLabel as RawFieldLabel,
  RadioGroupItem as RawRadioGroupItem,
  RadioGroupItemText as RawRadioGroupItemText,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { BidLimitHint } from '~/components/bidding/common/BidLimitHint'
import {
  RegisteredIssuerSelector,
  useRegisteredIssuerOptions,
} from '~/components/bidding/common/IssuerSelector'
import {
  type IssuerOption,
  findIssuerOption,
  isAreaSelectableForIssuer,
  issuerAreaDeliveryContractCode,
  issuerAreaSelectOptions,
} from '~/components/bidding/common/bidIssuer'
import { initialBidArea } from '~/components/bidding/common/lastBidArea'
import type { DsSelectOptions } from '~/components/common/DsSelect'
import { FormSelect } from '~/components/common/FormSelect'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import { useListTradableAreas } from '~/hooks/bidding/useListTradableAreas'
import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'
import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'
import {
  checkBidLimit,
  isMarketOrderAllowed,
  resolveAreaSideLimit,
} from '~/models/bidLimits'
import { now as serverNow } from '~/models/clock'
import { formatJstDateTimeHm } from '~/models/dateTime'
import { komaNumberTimeRange } from '~/models/komaToTime'
import {
  JEPX_PRICE_CONSTRAINT,
  JEPX_VOLUME_CONSTRAINT,
  isPlainDecimalInput,
  wrapNumberInputChange,
} from '~/models/numberInputConstraint'
import { IntradayAreaEmptyState } from '~/pages/IntradayBidding/components/IntradayAreaEmptyState'
import { IntradayFormulaFields } from '~/pages/IntradayBidding/components/IntradayFormulaFields'
import { IntradayPreparingRadioItem } from '~/pages/IntradayBidding/components/IntradayPreparingOption'
import { useIntradayFeeAdjustedPricePreview } from '~/pages/IntradayBidding/hooks/useIntradayFeeAdjustedPricePreview'
import type { IntradayFeeStateHandle } from '~/pages/IntradayBidding/hooks/useIntradayFeeState'
import { useIntradayFeeTreatment } from '~/pages/IntradayBidding/hooks/useIntradayFeeTreatment'
import { useIntradayFormulaFields } from '~/pages/IntradayBidding/hooks/useIntradayFormulaFields'
import { resolveIntradayAreaEmptyState } from '~/pages/IntradayBidding/models/areaEmptyState'
import { gcStatusOfKoma } from '~/pages/IntradayBidding/models/deliveryDate'
import {
  feeAdjustmentViolationMessage,
  feeAutoConsiderHint,
  opensAsFeeFallbackOff,
  toFeeAutoConsiderMatrixSetting,
  usesDesiredPrice,
} from '~/pages/IntradayBidding/models/feeAutoConsider'
import {
  EMPTY_FORMULA_FIELD_VALUES,
  parseIntradayFormulaFormValues,
} from '~/pages/IntradayBidding/models/intradayFormula'
import type { IntradayOrderCreatePrice } from '~/pages/IntradayBidding/models/intradayOrderWrite'
import { INTRADAY_P8_AVAILABLE } from '~/pages/IntradayBidding/models/intradayP8Availability'
import {
  intradayDesiredPriceViolation,
  intradayFormulaOffsetViolation,
  intradayPriceViolation,
  intradayVolumeViolation,
} from '~/pages/IntradayBidding/models/schema'
import {
  type WithdrawAtViolation,
  defaultWithdrawAtLocal,
  validateWithdrawAt,
  withdrawAtPickerRange,
} from '~/pages/IntradayBidding/models/withdrawAt'
import { routePaths } from '~/routes/routePaths'

/**
 * chakra v3 の `FieldLabelProps` / `FieldErrorTextProps` / `RadioGroupItem*Props` は
 * 本プロジェクトの型解決 (ecloud 0.14 / chakra 3.29 の d.ts 解決問題) で
 * `HTMLChakraProps` が collapse し `children` / 固有 prop が欠落する。 v3 本来の prop
 * シグネチャ (`HTMLChakraProps<element>`) へ cast して復元する。 ランタイムは ecloud のまま。
 */
const FieldLabelBase = RawFieldLabel as React.FC<HTMLChakraProps<'label'>>
// design `.form-label`: font-weight 600 / font-size md (14px) / high-emphasis。
// ecloud FieldLabel の既定は細め・小さめなので、 design に合わせて明示する。
const FieldLabel = (props: HTMLChakraProps<'label'>): React.ReactElement => (
  <FieldLabelBase
    textStyle="label"
    marginBottom={1}
    color="Text.highEmphasis"
    {...props}
  />
)
const FieldErrorText = RawFieldErrorText as React.FC<HTMLChakraProps<'div'>>
const RadioGroupItem = RawRadioGroupItem as React.FC<
  HTMLChakraProps<'div'> & { value: string; disabled?: boolean }
>
const RadioGroupItemText = RawRadioGroupItemText as React.FC<
  HTMLChakraProps<'span'>
>

/** 必須フィールドの赤い `*` (= design `.form-label.required` の擬似要素相当)。 */
const RequiredMark = (): React.ReactElement => (
  <Text as="span" marginLeft={1} color="Text.negativeMid" fontWeight="bold">
    *
  </Text>
)

const pad2 = (n: number): string => String(n).padStart(2, '0')

/** コマ別 GC 締切 ("HH:MM")。 GC = コマ開始 1 時間前 (= JEPX 時間前市場の原則)。 */
const komaGcDeadline = (koma: number): string => {
  const sh = Math.floor((koma - 1) / 2)
  const sm = (koma - 1) % 2 === 0 ? 0 : 30
  const gcH = (sh - 1 + 24) % 24
  return `${pad2(gcH)}:${pad2(sm)}`
}

/**
 * コマ select の GC 状態サフィックス。
 *
 * AC-BIN-014 (`docs/specs/bidding/intraday-bidding.md` L334-338): GC 通過済
 * (`closed`) は「選択肢から除外する（disabled 表示ではなく非表示、 誤選択自体を
 * 防ぐ）」ため、 呼び出し側 (`komaOptions`) が既に closed を配列から除外しており
 * ここには到達しない (= サフィックスを持たない)。 今まさに GC を迎える
 * (`imminent`) コマだけは選択肢に残るため「締切間近」を付ける。
 */
const komaGcSuffix = (
  status: ReturnType<typeof gcStatusOfKoma>,
  t: TFunction,
): string => {
  if (status === 'imminent')
    return ` ${t('page:biddingIntraday.bidCreate.komaGcNear')}`
  return ''
}

/** 入札追加 form の生入力 (= submit 前の string state)。 */
export type IntradayBidCreateFormInput = {
  timeCode: string // 1-48 (select)
  areaCode: string // 1-9 (select、 沖縄は時間前対象外)
  side: string // 1=買 / 2=売
  bidMode: string // normal=通常 / strategy=戦略
  volumeMw: string // MW
  priceType: string // fixed=固定価格 / formula=フォーミュラ / market=成行
  priceYenPerKwh: string // fixed のときの値 (円/kWh)
  formulaVariable: string // spot / id-last / id-high / id-low
  formulaReferenceArea: string // 1-9 / system (システムプライス)
  formulaOperator: string // + / - / * / /
  formulaOffset: string
  withdrawAt: string // datetime-local (任意)
  /**
   * 発注主体区分 (LicenseType enum number)。 入力欄は [[IssuerSelector]] で、 選んだ
   * 区分をそのまま書込の issuer_type として送る。
   */
  issuerType: number
}

/** submit 時に親へ渡す型 (= 数値変換済み)。 */
export type IntradayBidCreateResult = {
  timeCode: number
  areaCode: number
  side: number // 1=買 / 2=売
  bidMode: 'normal' | 'strategy'
  volumeMw: string
  /** 取り下げ時刻 (任意、 datetime-local 文字列)。 */
  withdrawAt?: string
  /** 発注主体 (= どの名義で JEPX に発注するか)。 LicenseType enum number。 */
  issuerType: number
  /** 作成 request の価格 (指値は手数料自動考慮の扱いつき、 AC-BIN-004b)。 */
  price: IntradayOrderCreatePrice
}

type Props = {
  isOpen: boolean
  /** 画面が開く前から取得している手数料自動考慮の状態 (開いた時に読む、 AC-BIN-004b)。 */
  feeState: IntradayFeeStateHandle
  /**
   * 対象受渡日。 コマ select の GC 状態判定と、 取り下げ時刻の GC 判定基準日の
   * 両方に使う (= 親 BidCreateTab が作成 RPC の `gcAt` を組むのと同じ日付)。
   * 受渡日は当日固定ではなく date picker / ワークフローケースの対象日で当日以外に
   * なり得るため、 modal 側で `new Date()` を代用できない。
   */
  deliveryDate: Date
  /** コマクリックからの prefill (= コマ / エリア / 売買 / 量)。 エリアが無ければ AC-BIN-122 の初期値。 */
  defaultValues?: Partial<IntradayBidCreateFormInput>
  /**
   * フォームで選んだエリアの**実際の**承認モードを解決する (issue #8312 の PdM 判断、
   * 2026-09-17)。
   *
   * 画面上部のエリアフィルタが「東京 (承認なし)」 でも、 本モーダルのエリア選択肢は
   * フィルタに縛られず JEPX 全 9 エリアから選べる (= 選択肢は制限しないのが同判断)。
   * そのため作成するエリアで承認が要るかを、 **確定前にモーダル内で案内**する。
   * (当初は手順表示 = stepper との食い違いを埋める目的だったが、 手順表示は 2026-10-05 の
   * PdM 判断で廃止した。 #8312)
   *
   * 親 (`BidCreateTab`) が承認フロー設定 (`GetApprovalFlowScreen`) から
   * `resolveIntradayApprovalMode` で解決する。 未指定 (= 設定が取得中・取得失敗で
   * 承認モードを確定できない間) は案内を出さない — その間は作成導線自体が非活性で、
   * 取得できない旨は画面側の Alert が伝えるため。
   */
  resolveApprovalMode?: (areaCode: AreaCode) => BiddingApprovalMode
  /**
   * 作成の確定。 返した Promise が settle するまで「作成」 ボタンを送信中にし、
   * 再度の押下を受け付けない (= 応答待ちの間の連打で同じ札を 2 件作らない、 #11548)。
   * 戻り値を Promise に限るのは、 確定を待たない handler (`void` を返す形) を渡すと
   * 送信中の印がすぐ外れて連打を止められないため (型検査で塞ぐ)。
   */
  onSubmit: (result: IntradayBidCreateResult) => Promise<unknown>
  onClose: () => void
}

const fieldsSchema = z.object({
  timeCode: z
    .string()
    .min(1, 'コマを選択してください')
    .refine((s) => {
      const n = Number(s)
      return Number.isInteger(n) && n >= 1 && n <= 48
    }, 'コマは 1〜48 の範囲で選択してください'),
  areaCode: z
    .string()
    .min(1, 'エリアを選択してください')
    .refine((s) => {
      const n = Number(s)
      return Number.isInteger(n) && n >= 1 && n <= 9
    }, 'エリアを選択してください'),
  // radio で '1' (買) / '2' (売) のみ。 default '1' なので空にはならない。
  side: z.string().min(1, '売/買を選択してください'),
  bidMode: z.string(),
  // 入力・保持・送信とも MW なので制度値域 (AC-038) をそのまま使う (issue #4285)。 判定は
  // 入札編集モーダルと共通 (`buildSchema` の superRefine)。
  volumeMw: z.string(),
  priceType: z.string(),
  priceYenPerKwh: z.string(),
  formulaVariable: z.string(),
  formulaReferenceArea: z.string(),
  formulaOperator: z.string(),
  formulaOffset: z.string(),
  withdrawAt: z.string(),
  issuerType: z.number().int().min(1, '発注主体を選択してください'),
})

// schema の出力は form の生入力型と一致する (= 数値・enum への変換は onValid で行う)。
type FormValues = IntradayBidCreateFormInput

/**
 * 入札追加 form の検証 schema。
 *
 * 取り下げ時刻の違反 message だけ引数で受ける (= 文言は app 規約どおり i18n 経由で、
 * component 側の `t` から渡す)。 他の message は spec §11 AC-BIN-052 が文言の SoT で
 * 既存実装が spec の文言をそのまま持つため、今回は i18n へ移送しない。
 */
const buildSchema = (
  withdrawAtMessage: (violation: WithdrawAtViolation) => string,
  deliveryDate: Date,
  isDesiredPrice: boolean,
): z.ZodType<FormValues, FormValues> =>
  fieldsSchema.superRefine((v, ctx) => {
    const volumeMessage = intradayVolumeViolation(v.volumeMw)
    if (volumeMessage !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['volumeMw'],
        message: volumeMessage,
      })
    }
    if (v.priceType === 'fixed') {
      // AC-BIN-052: ON の範囲外は入札価格の境界で判定する (AC-BIN-104、 onValid)。
      const priceMessage = isDesiredPrice
        ? intradayDesiredPriceViolation(v.priceYenPerKwh)
        : intradayPriceViolation(v.priceYenPerKwh)
      if (priceMessage !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['priceYenPerKwh'],
          message: priceMessage,
        })
      }
    } else if (v.priceType === 'formula') {
      const offsetMessage = intradayFormulaOffsetViolation(v.formulaOffset)
      if (offsetMessage !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['formulaOffset'],
          message: offsetMessage,
        })
      }
    }
    // priceType='market' は値入力なし (成行)。

    // 取り下げ (自動取消) 時刻は「現在時刻より後、対象コマの GC まで」でなければ
    // 意味を成さない (GC より後は札が終わっていて発火しない / 過去時刻の予約は発火しない)。
    // GC ちょうどは「GC と同時」 の自動取消として受け付ける (AC-BIN-017)。
    // GC の基準日は親 (BidCreateTab) が作成 RPC の `gcAt` を組むのに使うのと同じ
    // 受渡日 (deliveryDate) でなければならない。 当日 (`new Date()`) で代用すると、
    // 受渡日が翌日以降のとき正当な取り下げ時刻でも常に GC 超過と判定され、
    // 入札を追加できなくなる。
    // 現在時刻は schema 構築時ではなく検証時に読む (= modal を開いたまま時間が
    // 経過しても submit 時点の現在時刻で判定する)。
    const violation = validateWithdrawAt({
      withdrawAtLocal: v.withdrawAt,
      koma: Number(v.timeCode),
      deliveryDate,
      now: serverNow(),
    })
    if (violation !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['withdrawAt'],
        message: withdrawAtMessage(violation),
      })
    }
  })

const EMPTY_DEFAULTS: FormValues = {
  timeCode: '',
  areaCode: '',
  side: '1',
  bidMode: 'normal',
  volumeMw: '',
  priceType: 'fixed',
  priceYenPerKwh: '',
  ...EMPTY_FORMULA_FIELD_VALUES,
  withdrawAt: '',
  // 発注主体区分は空で開き、 選択肢が届いた時点で優先順の最上位を既定選択する
  // ([[IssuerSelector]] の `selectsDefault`、 AC-BIN-004)。
  issuerType: LicenseType.UNSPECIFIED,
}

/** 業務エリア設定に含まれないエリアか (AC-BIN-004 ※・決定 A)。 */
const isAreaOutsideBusinessAreas = (
  area: AreaCode,
  selectableAreaCodes: readonly AreaCode[],
): boolean => !selectableAreaCodes.includes(area)

/**
 * 時間前市場「入札追加」 modal。 design HTML `design/bidding/intraday.html` の
 * `#intraday-bid-new-modal` と項目を完全一致させる (= デザイン側が正):
 * コマ / エリア / 売買 / 入札方式 / 入札量(MW) / 入札価格(固定価格・フォーミュラ・成行) /
 * 取り下げ時刻 / タグ。
 *
 * submit は親 (= BidCreateTab) の onSubmit に結果を渡すのみ (= 共通方針、 RPC は親責務)。
 */
export const IntradayBidCreateModal = ({
  isOpen,
  feeState,
  deliveryDate,
  defaultValues,
  resolveApprovalMode,
  onSubmit,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // 入札制限 (settings「入札制限」INTRADAY segment、 スポットとは独立) の実設定値。
  // - AC-BIN-100: handleSave (onValid) が submit 直前に上限数量(1札) と照合してブロックする。
  //   取得中 (isLoading) は lookup が空のまま (= 制限なし相当) になり、 その間に submit
  //   すると上限超過でもチェックを素通りしてしまう (codex 指摘)。 取得中は保存ボタンを
  //   disabled にしてこの窓を塞ぐ。 取得失敗は意図的な
  //   fail-safe (= 札の作成時は入力補助でしかない。 提出時は提出確認モーダルが
  //   fail-closed で止める) なので disabled にしない。
  // - AC-BDL-014: 入札制限インラインヒント (下の BidLimitHint) も同じ lookup を引く。
  //   取得中 / 取得失敗の間は空 lookup を「制限未設定」 と出さず、 取得できていない旨を出す。
  //   ここでの表示は入力時の事前チェックで、 提出のブロックは提出確認モーダル
  //   (AC-BIN-015) と backend が行う。
  const {
    limits: bidLimits,
    isLoading: isBidLimitsLoading,
    isFetching: isBidLimitsFetching,
    isError: isBidLimitsError,
    marketOrderPolicyEntries,
    feeAutoConsiderEnabled,
    defaultWithdrawOffset: cachedDefaultWithdrawOffset,
  } = useBidLimits(BidSegment.INTRADAY)
  // AC-BIN-004 (#8738): 取り直し中・取得失敗中は cache に残る古い応答の値を初期値に使わない。
  const defaultWithdrawOffset =
    isBidLimitsFetching || isBidLimitsError
      ? undefined
      : cachedDefaultWithdrawOffset
  const feeMatrixSetting = toFeeAutoConsiderMatrixSetting({
    // 作成が拒否された後の取り直し中は、 取り直す前の ON/OFF とプレビューを突き合わせない。
    isLoading: isBidLimitsFetching,
    isError: isBidLimitsError,
    enabled: feeAutoConsiderEnabled,
  })
  // AC-BIN-004b: 手数料単価を取得できない間は OFF として扱い、 入力値 (フォーミュラは計算結果) を
  // そのまま入札価格にする。 欄名・検証も OFF のものにする。
  const fee = useIntradayFeeTreatment({
    isOpen,
    opensAsFallbackOff: opensAsFeeFallbackOff(feeState.state, feeMatrixSetting),
    setting: feeMatrixSetting,
    feeState,
  })
  const isFeeFallbackOff = fee.treatment.status === 'fallbackOff'
  const isDesiredPrice = usesDesiredPrice(fee.treatment, feeAutoConsiderEnabled)

  const schema = useMemo(
    () =>
      buildSchema(
        (violation): string => {
          switch (violation.kind) {
            case 'afterGc':
              return t('page:biddingIntraday.bidCreate.withdrawAtAfterGc', {
                gcAt: formatJstDateTimeHm(violation.gcAt),
              })
            case 'past':
              return t('page:biddingIntraday.bidCreate.withdrawAtPast')
          }
        },
        deliveryDate,
        isDesiredPrice,
      ),
    [t, deliveryDate, isDesiredPrice],
  )

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
    getValues,
    getFieldState,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { ...EMPTY_DEFAULTS, ...defaultValues },
  })

  // ----- 発注主体区分 -----
  // 区分の集合は受渡日によらない登録済み区分から作り、対象日のエリア可否を組み合わせる。
  // 選んだ区分をそのまま `issuerType` (= 書込の issuer_type) として送る。
  const issuerOptions = useRegisteredIssuerOptions(deliveryDate)
  const watchedIssuerType = watch('issuerType') as LicenseType
  // 選択中の発注主体区分 (AC-BIN-004 ※)。 区分が未選択のうちは undefined
  // (= まだ絞り込まない)。
  const selectedIssuer = findIssuerOption(issuerOptions, watchedIssuerType)
  const handleIssuerChange = (
    next: LicenseType,
    option: IssuerOption | undefined,
  ): void => {
    setValue('issuerType', next, { shouldValidate: true })
    // 選択中のエリアが新しい区分で選べなければ選択解除する
    // (AC-BIN-004 ※。 スポット AC-BSP-005b と同じ)。
    const currentArea = getValues('areaCode')
    if (
      option !== undefined &&
      currentArea !== '' &&
      !isAreaSelectableForIssuer(option, Number(currentArea) as AreaCode)
    ) {
      setValue('areaCode', '')
    }
  }

  // AC-BIN-004 (#8738): 自動取消時刻の初期値の元。 設定の取り直しや受渡日の変更で入れ直さないよう effect の依存にせず ref で読む。
  const withdrawAtBasisRef = useRef({
    offset: defaultWithdrawOffset,
    deliveryDate,
  })
  withdrawAtBasisRef.current = { offset: defaultWithdrawOffset, deliveryDate }
  // 利用者が欄を書き換えた (消した) か。 isDirty / isTouched は、 setValue で入れた値を消す操作を拾えない。
  const withdrawAtEditedRef = useRef(false)
  const issuerOptionsRef = useRef(issuerOptions)
  issuerOptionsRef.current = issuerOptions
  const { selectableAreaCodes, isBusinessAreasResolved } =
    useBusinessAreaFilter(JEPX_AREA_CODES)
  const { data: tradableAreasData } = useListTradableAreas(deliveryDate)
  const selectableAreaCodesRef = useRef(selectableAreaCodes)
  selectableAreaCodesRef.current = selectableAreaCodes

  useEffect(() => {
    if (isOpen) {
      withdrawAtEditedRef.current = false
      const values = { ...EMPTY_DEFAULTS, ...defaultValues }
      const issuer = findIssuerOption(
        issuerOptionsRef.current,
        values.issuerType as LicenseType,
      )
      const initialArea = initialBidArea(
        'intraday',
        values.areaCode ? (Number(values.areaCode) as AreaCode) : undefined,
        (code) =>
          isAreaSelectableForIssuer(issuer, code) &&
          selectableAreaCodesRef.current.includes(code),
      )
      reset({
        ...values,
        areaCode: initialArea === undefined ? '' : String(initialArea),
        withdrawAt: defaultWithdrawAtLocal({
          ...withdrawAtBasisRef.current,
          koma: Number(defaultValues?.timeCode),
        }),
      })
    }
  }, [isOpen, defaultValues, reset])

  // コマ select の GC 判定基準時刻。 AC-BIN-014 / AC-BIN-004 L167:
  // 「GC済判定はモーダルを開いた時点の時刻を基準に固定し、 モーダル表示中に時間が
  // 経過してもコマ選択肢を動的に更新しない」。 親から 1 分ごとに更新される時刻を
  // 直接使うと表示中に選択肢が動いてしまうため、 isOpen が false→true になった
  // 瞬間の時刻をここでスナップショットして固定する (モーダルは isOpen 制御で
  // unmount されないため、 mount 時の初期値だけでは開き直しに追従できない)。
  //
  // server time offset (`useServerTimeOffsetMs`) はここでは購読しない。 この値は
  // 「開いた時点」 で 1 度だけ評価する時刻依存の初期値であり、 offset の確定待ちと
  // 確定後の初期値再評価は `ServerClockGate` が 1 箇所で担う (通常経路は確定後に
  // 初回描画、 待ち上限経路は初期値が変わる大きさの offset が確定した時点で subtree を
  // 作り直す = 本モーダルも閉じて開き直しになる)。 確定後の定期補正 (TickerBar の
  // session 再取得) に追従して撮り直す / 差分補正すると、 本番では ms 単位の揺れで
  // 経過時間が載って上記 AC が破れ、 dev の固定時刻モードでは offset が経過時間ぶん
  // 縮んで基準が開いた時点より前へ巻き戻る (codex 指摘 2026-08-22) ため、 採らない。
  const [komaSelectAt, setKomaSelectAt] = useState<Date>(() => serverNow())

  // 撮り直しは paint 前 (useLayoutEffect) に行う。 useEffect だと開き直した直後の
  // 1 フレームだけ前回開いた時点の基準で選択肢が組まれ、 GC を越えて開き直した
  // 場合に GC 済コマが一瞬選択肢に並ぶ (codex 指摘 2026-08-22)。
  useLayoutEffect(() => {
    if (isOpen) {
      setKomaSelectAt(serverNow())
    }
  }, [isOpen])

  /**
   * コマ選択肢。 AC-BIN-014 (`docs/specs/bidding/intraday-bidding.md` L334-338、
   * Discussion 2026-08-18 決定 A): GC 通過済 (`closed`) は選択肢そのものから
   * 除外する (disabled 表示ではなく非表示、 誤選択自体を防ぐ)。 旧実装は
   * `disabled: true` で menu に残していたが、 disabled 表示では誤クリックを
   * 防げないため廃止した (= 参照実装: mock-app `BidEditDialog.razor` L47
   * `if (slot <= GcSlotIndex) continue;`)。
   * 基準時刻は `komaSelectAt` (= モーダルを開いた時点でスナップショットした時刻)
   * で固定し、 表示中に時間が経過しても選択肢を動的に更新しない (同 AC 後段)。
   */
  const komaOptions: DsSelectOptions = useMemo(
    () =>
      Array.from({ length: 48 }, (_, i) => i + 1)
        .map((k) => ({
          k,
          status: gcStatusOfKoma(komaSelectAt, deliveryDate, k),
        }))
        .filter(({ status }) => status !== 'closed')
        .map(({ k, status }) => ({
          value: String(k),
          label: `${k} (${komaNumberTimeRange(k, '〜')})${komaGcSuffix(status, t)}`,
        })),
    [komaSelectAt, deliveryDate, t],
  )

  const areaCode = watch('areaCode')
  const selectedArea =
    areaCode === '' ? undefined : (Number(areaCode) as AreaCode)

  // JEPX 受渡契約コード (AC-BIN-004)。 引く規則は `issuerAreaDeliveryContractCode`。
  const deliveryContractCode = issuerAreaDeliveryContractCode(
    selectedIssuer,
    selectedArea,
  )

  // エリア選択肢 (沖縄は時間前市場の対象外)。 form は string で持つので value も string
  // (`JEPX_AREA_CODES` は北海道=1〜九州=9 の 9 エリアで沖縄を含まないため、
  // `String(code)` がそのまま既存の '1'〜'9' 値と一致する)。 ラベルは
  // canonical な `convertAreaLabel` (= `common:area.*`) を SoT とする
  // (= `IntradayBulkCreateModal` と同じ組み合わせ)。
  // 未登録・業務エリア外の見せ方は AC-BIN-004 ※・決定 A (`issuerAreaSelectOptions`)。
  const areaOptions: DsSelectOptions = issuerAreaSelectOptions(
    selectedIssuer,
    JEPX_AREA_CODES,
    selectedArea,
    (area) => isAreaOutsideBusinessAreas(area, selectableAreaCodes),
    convertAreaLabel,
  ).map(({ area, label, disabled }) => ({
    value: String(area),
    label,
    disabled,
  }))
  const areaEmptyState = resolveIntradayAreaEmptyState({
    issuer: selectedIssuer,
    businessAreaCodes: isBusinessAreasResolved
      ? selectableAreaCodes
      : undefined,
    registrationIssues: tradableAreasData?.registrationIssues,
    jepxAreaCodes: JEPX_AREA_CODES,
  })

  // 業務エリア設定の取得後・変更後に対象外になったエリアは、 選択肢に残して押せなくしたうえで
  // form の値からも外す (AC-BIN-004 ※・決定 A)。 reset の同じ commit で走るため、
  // 描画時の値ではなく form の今の値を読む。
  useEffect(() => {
    const current = getValues('areaCode')
    if (
      current !== '' &&
      !selectableAreaCodes.includes(Number(current) as AreaCode)
    ) {
      setValue('areaCode', '')
    }
  }, [selectableAreaCodes, areaCode, getValues, setValue])

  const side = watch('side')
  const bidMode = watch('bidMode')
  const priceType = watch('priceType')
  const timeCode = watch('timeCode')
  const volumeMw = watch('volumeMw')
  const priceYenPerKwh = watch('priceYenPerKwh')
  const formulaVariable = watch('formulaVariable')
  const formulaReferenceArea = watch('formulaReferenceArea')
  const formulaOperator = watch('formulaOperator')
  const formulaOffset = watch('formulaOffset')

  // コマを選び直したら書き換えていても GC − N 分で入れ直し (mock-app の OnSlotChanged)、 出ているエラーだけ検証し直す。
  // 設定が決まらない間は前のコマの時刻を残さず空にし、 届いた時点で下の effect が入れる。
  useEffect(() => {
    withdrawAtEditedRef.current = false
    setValue(
      'withdrawAt',
      defaultWithdrawAtLocal({
        ...withdrawAtBasisRef.current,
        koma: Number(timeCode),
      }),
      { shouldValidate: getFieldState('withdrawAt').error !== undefined },
    )
  }, [timeCode, getFieldState, setValue])

  // 開いた後に設定が届いた時は、 欄が未編集の時だけ初期値を入れる。
  const previousOffsetRef = useRef(defaultWithdrawOffset)
  useEffect(() => {
    const arrived =
      previousOffsetRef.current === undefined &&
      defaultWithdrawOffset !== undefined
    previousOffsetRef.current = defaultWithdrawOffset
    if (!arrived || !isOpen || withdrawAtEditedRef.current) return
    setValue(
      'withdrawAt',
      defaultWithdrawAtLocal({
        ...withdrawAtBasisRef.current,
        koma: Number(getValues('timeCode')),
      }),
    )
  }, [defaultWithdrawOffset, isOpen, getValues, setValue])

  // ----- 入札制限インラインヒント (design intraday.html の制限 hint)。
  // エリア + 売/買 が確定した時点で制限を引く (どちらか未確定なら undefined = 非表示)。
  const resolvedLimit =
    areaCode !== '' && side !== ''
      ? resolveAreaSideLimit(
          bidLimits,
          Number(areaCode) as AreaCode,
          Number(side) as Side,
        )
      : undefined
  const hintVolume = volumeMw !== '' ? Number(volumeMw) : undefined

  // AC-BIN-004b / AC-BIN-104: 入札価格と値域・入札制限の判定は backend のプレビュー RPC
  // の結果を出す (#9213)。 固定価格でエリア・売買・価格が揃った時だけ問い合わせる。
  const feePreviewInput =
    priceType === 'fixed' &&
    areaCode !== '' &&
    (side === '1' || side === '2') &&
    isPlainDecimalInput(priceYenPerKwh)
      ? {
          areaCode: Number(areaCode) as AreaCode,
          direction: side === '2' ? Side.SELL : Side.BUY,
          desiredPriceYenPerKwh: priceYenPerKwh,
        }
      : undefined
  const feePreview = useIntradayFeeAdjustedPricePreview(feePreviewInput, fee)
  const feeHint = feeAutoConsiderHint(feePreview)

  // AC-BIN-005c: フォーミュラの計算結果は backend のプレビュー RPC の結果を出す。
  const formula = useIntradayFormulaFields({
    isOpen,
    deliveryDate,
    values: {
      priceType,
      timeCode,
      side,
      areaCode,
      formulaVariable,
      formulaReferenceArea,
      formulaOperator,
      formulaOffset,
    },
    fee,
  })

  // 入札制限ヒントの価格軸。 上下限価格は入札価格で判定する (domain-terms「入札価格」) ので、
  // ON は入力値 (希望価格) ではなくプレビューの入札価格を渡す。 ON で算出できていない間・
  // 範囲外の間は渡さず、 AC-BIN-104 の案内に任せる。 フォーミュラの入札価格は保存時に
  // backend が計算するので、 入力欄の値 (オフセット値) を制限と比較しない。
  const hintPrice = ((): number | undefined => {
    if (priceType !== 'fixed' || priceYenPerKwh === '') return undefined
    if (feePreview.kind === 'ready' && feePreview.confirmed.enabled) {
      return feePreview.orderPriceYenPerKwh !== undefined
        ? Number(feePreview.orderPriceYenPerKwh)
        : undefined
    }
    // ここに来る ready は OFF。 プレビューがまだ無い間は欄名と同じ ON/OFF で決める。
    const isOff = feePreview.kind === 'ready' || !isDesiredPrice
    return isOff ? Number(priceYenPerKwh) : undefined
  })()
  const isFeeAutoConsiderBlocking =
    (priceType === 'fixed' && feePreview.kind === 'blocked') ||
    formula.submit.kind === 'feeBlocked'

  // 成行注文許可 (AC-BIN-004d、 SoT は `bid-limits.md` §2-1 AC-BDL-012)。
  // エリア未選択 / 取得中・取得失敗はいずれも false (= fail-safe、
  // [[isMarketOrderAllowed]] 参照)。
  const marketAllowed =
    areaCode !== '' &&
    isMarketOrderAllowed(
      marketOrderPolicyEntries,
      BidSegment.INTRADAY,
      Number(areaCode) as AreaCode,
    )

  // AC-BIN-052: 成行が選ばれたまま成行禁止エリアになった時に出す検証エラーの対象
  // エリア。 エリアを変えたら消す (下のフォールバックより先に宣言し、 同じ commit
  // 内では宣言順に実行されるのでフォールバックが出し直したものが勝つ)。 価格種別の
  // 選び直し・価格欄の入力・作成の押下でも消し、 以降は価格欄の検証エラーを出す。
  const [marketForbiddenArea, setMarketForbiddenArea] = useState<
    string | undefined
  >(undefined)
  useEffect(() => {
    setMarketForbiddenArea((prev) => (prev === areaCode ? prev : undefined))
  }, [areaCode])
  useEffect(() => {
    if (isOpen) {
      setMarketForbiddenArea(undefined)
    }
  }, [isOpen])

  // 成行選択中にエリアを変更して成行注文許可が無くなった場合、 固定価格へ自動
  // フォールバックし、 戻した理由として入札制限の許可設定を参照した AC-BIN-052 の
  // エラーを価格欄に出す (SpotBidCreateModal と同じ挙動 + 理由の明示)。
  // 取得中 (isBidLimitsLoading) の間は判定不能であって「不許可」ではないため、
  // 取得完了を待ってから評価する。
  useEffect(() => {
    if (!isBidLimitsLoading && priceType === 'market' && !marketAllowed) {
      setValue('priceType', 'fixed', { shouldValidate: true })
      if (areaCode !== '') {
        setMarketForbiddenArea(areaCode)
      }
    }
  }, [isBidLimitsLoading, priceType, marketAllowed, setValue, areaCode])
  const marketForbiddenMessage =
    marketForbiddenArea !== undefined && !marketAllowed
      ? t('page:biddingIntraday.form.marketOrderForbiddenError', {
          area: convertAreaLabel(Number(marketForbiddenArea) as AreaCode),
        })
      : undefined

  // 選んだエリアの実際の承認モード (issue #8312、 [[Props.resolveApprovalMode]] 参照)。
  // エリア未選択の間と、 親が承認モードを確定できない間 (= prop 未指定) は案内を出さない。
  const areaApprovalMode =
    areaCode !== ''
      ? resolveApprovalMode?.(Number(areaCode) as AreaCode)
      : undefined

  // AC-3304-2: 入札量・固定価格入力欄は JEPX 範囲外・不正な小数点桁の入力を受け付けない。
  const volumeMwReg = register('volumeMw')
  const priceYenPerKwhReg = register('priceYenPerKwh')

  const onValid = (values: FormValues): void | Promise<unknown> => {
    // AC-BIN-100: 入札量が上限数量(1札) を超える場合は保存をブロックする
    // (= スポット入札 AC-BSP-103 と同型)。 常時表示は BidLimitHint が担い、 ここでは
    // submit 時のブロックと文言表示を行う。 クランプは行わない (bid-limits.md §2-2)。
    const limitForSubmit = resolveAreaSideLimit(
      bidLimits,
      Number(values.areaCode) as AreaCode,
      Number(values.side) as Side,
    )
    const submitVolumeMw = Number(values.volumeMw)
    if (
      checkBidLimit(undefined, submitVolumeMw, limitForSubmit).includes(
        'volume-max',
      )
    ) {
      setError('volumeMw', {
        message: t('page:biddingIntraday.form.volumeExceedsPerBidLimit', {
          max: limitForSubmit?.qtyMax,
          value: submitVolumeMw,
        }),
      })
      return
    }
    const common = {
      timeCode: Number(values.timeCode),
      areaCode: Number(values.areaCode),
      side: Number(values.side),
      bidMode: values.bidMode === 'strategy' ? 'strategy' : 'normal',
      volumeMw: values.volumeMw,
      withdrawAt: values.withdrawAt === '' ? undefined : values.withdrawAt,
      issuerType: values.issuerType,
    } as const
    if (values.priceType === 'formula') {
      const formulaInput = parseIntradayFormulaFormValues(values)
      if (formulaInput === undefined) {
        setError('root.serverError', {
          message: t('page:biddingIntraday.serverError.validation'),
        })
        return
      }
      // AC-BIN-004b / AC-BIN-104: 計算結果から求めた入札価格が範囲外なら、 固定価格と同じく止めて案内する。
      if (formula.submit.kind === 'outOfRange') {
        setError('formulaOffset', {
          message: feeAdjustmentViolationMessage(t, formula.submit.violation),
        })
        return
      }
      return onSubmit({
        ...common,
        price: {
          kind: 'formula',
          formula: formulaInput,
          treatFeeAutoConsiderAsOff: isFeeFallbackOff,
        },
      })
    }
    if (values.priceType === 'market') {
      return onSubmit({ ...common, price: { kind: 'market' } })
    }
    switch (feePreview.kind) {
      case 'outOfRange':
        setError('priceYenPerKwh', {
          message: feeAdjustmentViolationMessage(t, feePreview.violation),
        })
        return
      case 'fallbackOff':
        return onSubmit({
          ...common,
          price: {
            kind: 'orderPrice',
            orderPriceYenPerKwh: values.priceYenPerKwh,
          },
        })
      case 'ready':
        return onSubmit({
          ...common,
          price: {
            kind: 'limit',
            desiredPriceYenPerKwh: values.priceYenPerKwh,
            feeAutoConsider: feePreview.confirmed,
          },
        })
      default:
        // blocked の間は作成ボタンを押せず、 idle (入力不足) は schema の検証が先に止める。
        return
    }
  }

  // 取り下げ時刻の違反も `withdrawAt` 直下なので項目脇に出る。
  const onInvalid = (formErrors: FieldErrors<FormValues>): void => {
    const alertMessage = resolveFormErrorAlertMessage(
      formErrors,
      t('page:biddingIntraday.serverError.validation'),
    )
    if (alertMessage !== undefined) {
      setError('root.serverError', { message: alertMessage })
    } else {
      // 直前の失敗で出した alert が残り続けるのを防ぐ。
      clearErrors('root.serverError')
    }
  }

  const validationError = errors.root?.serverError?.message

  // 作成の確定中フラグ (#11548)。 state は「作成」 ボタンの送信中表示、 ref は再入判定に使う。
  // ref が要るのは、 同一 tick 内の連打では 2 回目の押下が 1 回目と同じ render の closure を
  // 見るため state では防げないから (`SpotBidCreateModal` の savingRef と同じ理由)。
  const [isSaving, setIsSaving] = useState(false)
  const savingRef = useRef(false)
  const handleSave = async (): Promise<void> => {
    // 二重防御。 送信中と手数料設定が読めない間は押下を受け付けない
    // (入札制限の読み込み中は footer の isSaveDisabled だけで止める)。
    if (savingRef.current || isFeeAutoConsiderBlocking) return
    savingRef.current = true
    setIsSaving(true)
    try {
      setMarketForbiddenArea(undefined)
      await handleSubmit(onValid, onInvalid)()
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }

  const selectedKoma = Number(timeCode)
  const gcHint =
    Number.isInteger(selectedKoma) && selectedKoma >= 1 && selectedKoma <= 48
      ? komaGcDeadline(selectedKoma)
      : '—'
  // 取り下げ時刻ピッカーは「現在時刻より後 〜 対象コマの GC」だけ選べる (#10106)。
  const withdrawAtRange = withdrawAtPickerRange({
    koma: selectedKoma,
    deliveryDate,
    now: serverNow(),
  })

  return (
    <Modal
      footer={
        <SaveCancelFooter
          isPending={isSaving}
          isSaveDisabled={
            areaEmptyState !== undefined ||
            isSaving ||
            isBidLimitsLoading ||
            isFeeAutoConsiderBlocking
          }
          onCancel={onClose}
          onSave={() => {
            void handleSave()
          }}
          saveLabel={t('page:biddingCommon.modal.create')}
        />
      }
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="md"
      title={t('page:biddingIntraday.modal.createTitle')}
    >
      {areaEmptyState !== undefined ? (
        <VStack align="stretch" gap={4}>
          <RegisteredIssuerSelector
            deliveryDate={deliveryDate}
            errorMessage={errors.issuerType?.message}
            onChange={handleIssuerChange}
            selectsDefault
            value={watchedIssuerType}
          />
          <IntradayAreaEmptyState state={areaEmptyState} />
        </VStack>
      ) : (
        <VStack align="stretch" gap={4}>
          {validationError !== undefined && (
            <Alert status="error" title={validationError} />
          )}

          {/* 発注主体区分。 全入札 UI 共通の [[IssuerSelector]] を使う。
            以前は画面上部に置かれ、 選んでも札には残らなかった (issue #5366)。 */}
          <RegisteredIssuerSelector
            deliveryDate={deliveryDate}
            errorMessage={errors.issuerType?.message}
            onChange={handleIssuerChange}
            selectsDefault
            value={watchedIssuerType}
          />

          {/* JEPX 受渡契約コード (AC-BIN-004)。 design と同じく発注主体区分の直下。 入力ではなく
            参照表示なので素のテキスト (design/CLAUDE.md 「参照表示」 規約)。 未解決の「—」 は
            値と描き分ける (design の _updateBnewDeliveryCd)。 */}
          <FieldRoot>
            <FieldLabel>
              {t('page:biddingIntraday.form.deliveryContractCode')}
            </FieldLabel>
            <Text
              color={
                deliveryContractCode === undefined
                  ? 'Text.lowEmphasis'
                  : 'Text.highEmphasis'
              }
              fontWeight="bold"
              data-testid="intradayCreate-deliveryContractCode"
              fontVariantNumeric="tabular-nums"
            >
              {deliveryContractCode ??
                t('page:biddingIntraday.form.deliveryContractCodeEmpty')}
            </Text>
          </FieldRoot>

          {/* コマ */}
          <FieldRoot invalid={errors.timeCode !== undefined} required>
            <FieldLabel>
              {t('page:biddingIntraday.form.timeCode')}
              <RequiredMark />
            </FieldLabel>
            {/* native 時代の `<option disabled value="">選択</option>` は placeholder へ
              (option として残すと「選択」が選択肢に並ぶため)。 GC 通過済コマは
              AC-BIN-014 に従い選択肢そのものから除外する (disabled 表示ではなく
              非表示、 誤選択自体を防ぐ。 `komaOptions` 側で filter 済み)。 */}
            <FormSelect
              aria-label={t('page:biddingIntraday.form.timeCode')}
              control={control}
              name="timeCode"
              options={komaOptions}
              placeholder={t('common:form.selectPlaceholder')}
            />
            <FieldErrorText>{errors.timeCode?.message}</FieldErrorText>
          </FieldRoot>

          {/* エリア (沖縄は時間前対象外) */}
          <FieldRoot invalid={errors.areaCode !== undefined} required>
            <FieldLabel>
              {t('page:biddingIntraday.form.areaCode')}
              <RequiredMark />
            </FieldLabel>
            <FormSelect
              aria-label={t('page:biddingIntraday.form.areaCode')}
              control={control}
              name="areaCode"
              options={areaOptions}
              placeholder={t('common:form.selectPlaceholder')}
            />
            {/* 選んだエリアの承認要否の案内 (issue #8312 PdM 判断)。 手順表示 (stepper) は
              画面上部のエリアフィルタ基準のままなので、 フィルタ外のエリアを選んだ時に
              実際の承認要否をここで示す。 文字スタイルは同モーダルの既存ヒント
              (成行禁止の案内) と揃える。 */}
            {areaApprovalMode !== undefined && (
              <Text marginTop={1} color="Text.midEmphasis" fontSize="sm">
                {t(
                  areaApprovalMode === BiddingApprovalMode.APPROVAL
                    ? 'page:biddingIntraday.form.approvalModeRequired'
                    : 'page:biddingIntraday.form.approvalModeNotRequired',
                )}
              </Text>
            )}
            <FieldErrorText>{errors.areaCode?.message}</FieldErrorText>
          </FieldRoot>

          {/* 売/買 (色付き badge radio) */}
          <FieldRoot invalid={errors.side !== undefined} required>
            <FieldLabel>
              {t('page:biddingIntraday.form.direction')}
              <RequiredMark />
            </FieldLabel>
            <RadioGroupRoot
              onValueChange={(details: { value: string | null }) =>
                setValue('side', details.value ?? '', {
                  shouldValidate: true,
                })
              }
              value={side}
            >
              <HStack gap={4}>
                <RadioGroupItem value="1">
                  <RadioGroupItemHiddenInput />
                  <RadioGroupItemControl />
                  <RadioGroupItemText>
                    {/* RadioGroup は選択肢が固定 (買/売の2択) なので form の string 値
                      ('1'/'2') を enum へ変換せず、enum リテラルを直接渡す
                      (design: 変換経路を増やさない)。 */}
                    <TradeSideLabel side={Side.BUY} />
                  </RadioGroupItemText>
                </RadioGroupItem>
                <RadioGroupItem value="2">
                  <RadioGroupItemHiddenInput />
                  <RadioGroupItemControl />
                  <RadioGroupItemText>
                    <TradeSideLabel side={Side.SELL} />
                  </RadioGroupItemText>
                </RadioGroupItem>
              </HStack>
            </RadioGroupRoot>
            <FieldErrorText>{errors.side?.message}</FieldErrorText>
          </FieldRoot>

          {/* 入札方式 (通常 / 戦略 / アイスバーグ)。 戦略・アイスバーグは P8 の機能で、 選択肢に出した
            まま非活性にし、 理由は名前側のツールチップで示す (AC-BIN-133)。 戦略は
            `INTRADAY_P8_AVAILABLE` が true の間は選べる。 アイスバーグは実装が無いので出さない。 */}
          <FieldRoot>
            <FieldLabel>{t('page:biddingIntraday.form.bidMode')}</FieldLabel>
            <RadioGroupRoot
              onValueChange={(details: { value: string | null }) =>
                setValue('bidMode', details.value ?? '')
              }
              value={bidMode}
            >
              <HStack gap={4}>
                <RadioGroupItem value="normal">
                  <RadioGroupItemHiddenInput />
                  <RadioGroupItemControl />
                  <RadioGroupItemText>
                    {t('page:biddingIntraday.form.bidModeNormal')}
                  </RadioGroupItemText>
                </RadioGroupItem>
                <IntradayPreparingRadioItem
                  label={t('page:biddingIntraday.form.bidModeStrategy')}
                  value="strategy"
                />
                {!INTRADAY_P8_AVAILABLE && (
                  <IntradayPreparingRadioItem
                    label={t('page:biddingIntraday.form.bidModeIceberg')}
                    value="iceberg"
                  />
                )}
              </HStack>
            </RadioGroupRoot>
          </FieldRoot>

          {/* 入札量 (MW) */}
          <FieldRoot invalid={errors.volumeMw !== undefined} required>
            <FieldLabel htmlFor="intradayBid-volumeMw">
              {t('page:biddingIntraday.form.volumeMwh')}
              <RequiredMark />
            </FieldLabel>
            <HStack gap={2}>
              <Input
                flex={1}
                fontSize="md"
                textAlign="right"
                max={JEPX_VOLUME_CONSTRAINT.max}
                min={JEPX_VOLUME_CONSTRAINT.min}
                paddingY="11px"
                step={JEPX_VOLUME_CONSTRAINT.step}
                type="number"
                {...volumeMwReg}
                id="intradayBid-volumeMw"
                onChange={wrapNumberInputChange(
                  volumeMwReg,
                  JEPX_VOLUME_CONSTRAINT,
                )}
              />
              <Text color="Text.midEmphasis" fontSize="md">
                MW
              </Text>
            </HStack>
            <FieldErrorText>{errors.volumeMw?.message}</FieldErrorText>
          </FieldRoot>

          {/* 入札価格 (固定価格 / フォーミュラ〈準備中〉/ 成行) */}
          <FieldRoot
            invalid={
              errors.priceYenPerKwh !== undefined ||
              errors.formulaOffset !== undefined ||
              marketForbiddenMessage !== undefined
            }
            required
          >
            <FieldLabel>
              {t(
                isDesiredPrice
                  ? 'page:biddingIntraday.form.desiredPriceYenPerKwh'
                  : 'page:biddingIntraday.form.priceYenPerKwh',
              )}
              <RequiredMark />
            </FieldLabel>
            <RadioGroupRoot
              onValueChange={(details: { value: string | null }) => {
                setMarketForbiddenArea(undefined)
                setValue('priceType', details.value ?? '')
              }}
              value={priceType}
            >
              <HStack flexWrap="wrap" gap={4}>
                <RadioGroupItem value="fixed">
                  <RadioGroupItemHiddenInput />
                  <RadioGroupItemControl />
                  <RadioGroupItemText>
                    {t('page:biddingIntraday.form.priceTypeFixed')}
                  </RadioGroupItemText>
                </RadioGroupItem>
                {/* フォーミュラは P8 の機能 (AC-BIN-133)。 */}
                <IntradayPreparingRadioItem
                  label={t('page:biddingIntraday.form.priceTypeFormula')}
                  value="formula"
                />
                {/* 成行はエリア別の成行注文許可設定 (`bid-limits.md` §2-1 AC-BDL-012
                  が SoT) に従い選択肢をグレーアウトする (AC-BIN-004d)。 解放動線は
                  下のヒントで案内。 */}
                <RadioGroupItem disabled={!marketAllowed} value="market">
                  <RadioGroupItemHiddenInput />
                  <RadioGroupItemControl />
                  <RadioGroupItemText
                    color={marketAllowed ? undefined : 'Text.disabled'}
                  >
                    {t('page:biddingIntraday.form.priceTypeMarket')}
                  </RadioGroupItemText>
                </RadioGroupItem>
              </HStack>
            </RadioGroupRoot>

            {priceType === 'fixed' && (
              <HStack gap={2} marginTop={2}>
                <Input
                  flex={1}
                  fontSize="md"
                  textAlign="right"
                  max={JEPX_PRICE_CONSTRAINT.max}
                  min={JEPX_PRICE_CONSTRAINT.min}
                  paddingY="11px"
                  step={JEPX_PRICE_CONSTRAINT.step}
                  type="number"
                  {...priceYenPerKwhReg}
                  aria-label={t(
                    isDesiredPrice
                      ? 'page:biddingIntraday.form.desiredPriceAriaLabel'
                      : 'page:biddingIntraday.form.fixedPriceAriaLabel',
                  )}
                  id="intradayBid-priceYenPerKwh"
                  onChange={(event) => {
                    setMarketForbiddenArea(undefined)
                    wrapNumberInputChange(
                      priceYenPerKwhReg,
                      JEPX_PRICE_CONSTRAINT,
                    )(event)
                  }}
                />
                <Text color="Text.midEmphasis" fontSize="md">
                  {t('page:biddingIntraday.market.board.priceYenKwh')}
                </Text>
              </HStack>
            )}

            {/* AC-BIN-004b: 入札価格の補足表示 (design intraday.html #intraday-bnew-fee-preview)。 */}
            {priceType === 'fixed' && (
              <Text marginTop={1} color={feeHint.color} fontSize="sm">
                {t(`page:biddingIntraday.form.${feeHint.key}`, {
                  price: feeHint.orderPriceYenPerKwh,
                })}
              </Text>
            )}

            {priceType === 'formula' && (
              <IntradayFormulaFields
                control={control}
                fields={formula}
                offsetError={errors.formulaOffset?.message}
                offsetInputId="intradayBid-formulaOffset"
                onOffsetEdited={() => setMarketForbiddenArea(undefined)}
                register={register}
                setValue={setValue}
              />
            )}

            {areaCode !== '' && !marketAllowed && (
              <Text marginTop={1} color="Text.midEmphasis" fontSize="sm">
                {t('page:biddingIntraday.form.marketOrderHintPrefix', {
                  area: convertAreaLabel(Number(areaCode) as AreaCode),
                })}
                {/* design bidding/intraday.html の同文 hint は `.ecl-text-link`。
                  色・下線は DS Link 既定が design 値と一致するので指定しない。 */}
                <Link asChild>
                  <RouterLink
                    to={`${routePaths.adminBidLimits.path}?section=bid-limit-intraday`}
                  >
                    {t('page:biddingIntraday.form.marketOrderHintLink')}
                  </RouterLink>
                </Link>
                {t('page:biddingIntraday.form.marketOrderHintSuffix')}
              </Text>
            )}

            {priceType !== 'formula' && (
              <FieldErrorText whiteSpace="pre-line">
                {marketForbiddenMessage ?? errors.priceYenPerKwh?.message}
              </FieldErrorText>
            )}
          </FieldRoot>

          {/* 入札制限インラインヒント (AC-BDL-014: 入札制限画面で保存した 時間前 の
            上下限価格・上限数量をそのまま参照する)。 エリア + 売/買 確定後に表示。 */}
          {areaCode !== '' && side !== '' && (
            <BidLimitHint
              limit={resolvedLimit}
              price={hintPrice}
              unavailable={
                isBidLimitsLoading
                  ? 'loading'
                  : isBidLimitsError
                    ? 'error'
                    : undefined
              }
              volumeMw={hintVolume}
            />
          )}

          {/* 取り下げ時刻 (任意) */}
          <FieldRoot invalid={errors.withdrawAt !== undefined}>
            <FieldLabel htmlFor="intradayBid-withdrawAt">
              {t('page:biddingIntraday.form.withdrawAt')}
            </FieldLabel>
            <HStack flexWrap="wrap" gap={3}>
              <Input
                flex={1}
                minWidth="180px"
                fontSize="md"
                max={withdrawAtRange.max}
                min={withdrawAtRange.min}
                paddingY="11px"
                type="datetime-local"
                {...register('withdrawAt', {
                  onChange: () => {
                    withdrawAtEditedRef.current = true
                  },
                })}
                id="intradayBid-withdrawAt"
              />
              <Text color="Text.midEmphasis" fontSize="sm" whiteSpace="nowrap">
                GC {gcHint}
              </Text>
            </HStack>
            <FieldErrorText>{errors.withdrawAt?.message}</FieldErrorText>
          </FieldRoot>
        </VStack>
      )}
    </Modal>
  )
}
